import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ObjectCardModal } from '../shared/ObjectCardModal';
import { ObjectPhoto } from '../shared/ObjectPhoto';
import { ExportSelectionIcon, TerritoryCalculatorIcon } from '../icons/MapToolIcons';
import { useAdminData } from '../../admin/AdminDataContext';
import { dictionariesApi, type DictionaryValue } from '../../lib/dictionariesApi';
import { useMapContext } from '../../map/MapContext';
import type { DemoFeatureProps } from '../../map/demoLayers';

function formatLength(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(2)} км` : `${m.toFixed(0)} м`;
}

function formatArea(m2: number): string {
  if (m2 >= 10_000) return `${(m2 / 10_000).toFixed(2)} га`;
  if (m2 >= 100) return `${(m2 / 100).toFixed(2)} сот.`;
  return `${m2.toFixed(1)} м²`;
}

function formatLonLat([lon, lat]: [number, number]): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

const KNOWN_KEYS = ['name', 'objectType', 'registryNumber', 'address', 'areaM2', 'updatedAt'];

function pickTitle(props: DemoFeatureProps): string {
  if (typeof props.name === 'string' && props.name) return props.name;
  const firstString = Object.values(props).find((v): v is string => typeof v === 'string' && v.length > 0);
  return firstString ?? 'Объект без названия';
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'да' : 'нет';
  if (typeof value === 'number') return value.toLocaleString('ru-RU');
  return String(value);
}

export function SelectedObjectCard() {
  const {
    selectedFeature,
    selectedFeatureId,
    selectedFeatureLayerId,
    selectedFeaturePhotoFileId,
    selectedFeatureMetrics,
    layers,
    clearSelection,
    selectionResults,
    selectionIndex,
    setSelectionIndex,
    objectCalculatorEnabled,
    toggleObjectCalculator,
  } = useMapContext();
  const { mapSettings } = useAdminData();
  const tools = mapSettings?.tools;
  const [cardModalOpen, setCardModalOpen] = useState(false);

  const layerFields = layers.find((l) => l.id === selectedFeatureLayerId)?.fields ?? [];
  const dictionaryIdByCode = new Map(
    layerFields
      .filter((f) => f.data_type === 'dictionary' && f.dictionary_id)
      .map((f) => [f.code, f.dictionary_id as string]),
  );

  const [dictValuesByDict, setDictValuesByDict] = useState<Record<string, DictionaryValue[]>>({});
  useEffect(() => {
    for (const dictId of new Set(dictionaryIdByCode.values())) {
      if (dictValuesByDict[dictId]) continue;
      dictionariesApi
        .listValues(dictId)
        .then((vals) => setDictValuesByDict((prev) => ({ ...prev, [dictId]: vals })))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeatureLayerId]);

  if (!selectedFeature) return null;

  const title = pickTitle(selectedFeature);
  const hasKnownFields = typeof selectedFeature.address === 'string' || typeof selectedFeature.registryNumber === 'string';
  const extraEntries = Object.entries(selectedFeature).filter(
    ([key, value]) => !KNOWN_KEYS.includes(key) && value !== undefined && value !== null && value !== '',
  );

  const hasMultiSelection = selectionResults.length > 1;

  return (
    <>
    // z-30 — above LayersDrawer's open panel (z-20, also anchored left) so
    // selecting an object always brings its preview to front instead of it
    // rendering underneath an open layers panel.
    <div className="absolute bottom-6 left-6 z-30 w-[min(360px,calc(100vw-48px))] rounded-2xl border border-border-default bg-bg-surface p-5 shadow-lg">
      {hasMultiSelection && (
        <div className="mb-3 flex items-center justify-between gap-2 rounded-lg bg-bg-surface-secondary px-2 py-1.5">
          <button
            type="button"
            onClick={() => setSelectionIndex(selectionIndex - 1)}
            disabled={selectionIndex === 0}
            className="flex size-6 items-center justify-center rounded-md text-text-primary hover:bg-bg-surface disabled:opacity-30"
          >
            ‹
          </button>
          <span className="text-[11px] font-medium text-text-secondary">
            {selectionIndex + 1} из {selectionResults.length}
          </span>
          <button
            type="button"
            onClick={() => setSelectionIndex(selectionIndex + 1)}
            disabled={selectionIndex === selectionResults.length - 1}
            className="flex size-6 items-center justify-center rounded-md text-text-primary hover:bg-bg-surface disabled:opacity-30"
          >
            ›
          </button>
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <ObjectPhoto fileId={selectedFeaturePhotoFileId} canEdit={false} size={44} />
          <div className="min-w-0">
            {typeof selectedFeature.objectType === 'string' && (
              <p className="text-[11px] font-medium text-text-secondary">{selectedFeature.objectType}</p>
            )}
            <h3 className="mt-0.5 truncate text-base font-semibold text-text-primary">{title}</h3>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {tools?.objectCalculator !== false && (
            <button
              type="button"
              onClick={toggleObjectCalculator}
              title="Объектный калькулятор"
              className={`flex size-7 items-center justify-center rounded-lg ${
                objectCalculatorEnabled ? 'bg-action-primary text-text-inverse' : 'text-text-secondary hover:bg-bg-surface-secondary'
              }`}
            >
              <TerritoryCalculatorIcon className="size-4" />
            </button>
          )}
          {tools?.exportSelection !== false && (
            <button
              type="button"
              title="Экспортировать выборку — скоро"
              disabled
              className="flex size-7 items-center justify-center rounded-lg text-text-secondary disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ExportSelectionIcon className="size-4" />
            </button>
          )}
          <button
            type="button"
            onClick={clearSelection}
            title="Закрыть"
            className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="mt-4 flex max-h-[260px] flex-col gap-2.5 overflow-y-auto">
        {hasKnownFields && (
          <>
            {typeof selectedFeature.address === 'string' && <Row label="Адрес" value={selectedFeature.address} />}
            {typeof selectedFeature.registryNumber === 'string' && (
              <Row label="Кадастровый номер" value={selectedFeature.registryNumber} />
            )}
            {typeof selectedFeature.areaM2 === 'number' && (
              <Row label="Площадь" value={`${selectedFeature.areaM2.toLocaleString('ru-RU')} м²`} />
            )}
            {typeof selectedFeature.updatedAt === 'string' && <Row label="Обновлено" value={selectedFeature.updatedAt} />}
          </>
        )}
        {extraEntries.map(([key, value]) => {
          const dictId = dictionaryIdByCode.get(key);
          const fieldLabel = layerFields.find((f) => f.code === key)?.name ?? key;
          if (dictId) {
            const matched = typeof value === 'string' ? dictValuesByDict[dictId]?.find((v) => v.id === value) : undefined;
            return (
              <div key={key} className="flex items-center justify-between gap-4">
                <span className="shrink-0 text-xs text-text-secondary">{fieldLabel}</span>
                {matched ? (
                  <Link
                    to={`/app/dictionaries/${dictId}/values/${matched.id}`}
                    className="truncate text-xs font-medium text-action-primary hover:underline"
                  >
                    {matched.name}
                  </Link>
                ) : (
                  <span className="truncate text-xs font-medium text-text-primary">—</span>
                )}
              </div>
            );
          }
          return <Row key={key} label={fieldLabel} value={formatValue(value)} />;
        })}
        {!hasKnownFields && extraEntries.length === 0 && (
          <p className="text-xs text-text-secondary">У этого объекта нет атрибутов</p>
        )}
      </div>

      {objectCalculatorEnabled && selectedFeatureMetrics && (
        <div className="mt-3 flex flex-col gap-2 rounded-lg bg-bg-surface-secondary p-3">
          <p className="text-[11px] font-semibold text-text-primary">Измерения объекта</p>
          {selectedFeatureMetrics.kind === 'point' ? (
            <Row label="Координаты" value={formatLonLat([selectedFeatureMetrics.lon, selectedFeatureMetrics.lat])} />
          ) : selectedFeatureMetrics.kind === 'line' ? (
            <>
              <Row label="Длина" value={formatLength(selectedFeatureMetrics.lengthM)} />
              <Row label="Начальная точка" value={formatLonLat(selectedFeatureMetrics.start)} />
              <Row label="Конечная точка" value={formatLonLat(selectedFeatureMetrics.end)} />
            </>
          ) : (
            <>
              <Row label="Площадь" value={formatArea(selectedFeatureMetrics.areaM2)} />
              <Row label="Периметр" value={formatLength(selectedFeatureMetrics.perimeterM)} />
              <div className="flex flex-col gap-1">
                <span className="text-xs text-text-secondary">Поворотные точки ({selectedFeatureMetrics.vertices.length})</span>
                <div className="flex max-h-20 flex-col gap-0.5 overflow-y-auto">
                  {selectedFeatureMetrics.vertices.map((v, i) => (
                    <span key={i} className="text-[11px] font-medium text-text-primary">
                      {i + 1}. {formatLonLat(v)}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {objectCalculatorEnabled && !selectedFeatureMetrics && (
        <p className="mt-3 rounded-lg bg-bg-surface-secondary p-3 text-[11px] text-text-secondary">
          Измерения доступны для точек, линий и полигонов, выбранных напрямую кликом на карте.
        </p>
      )}

      {selectedFeatureLayerId && selectedFeatureId ? (
        <button
          type="button"
          onClick={() => setCardModalOpen(true)}
          className="mt-4 flex w-full items-center justify-center rounded-lg bg-action-primary py-2.5 text-sm font-semibold text-text-inverse transition-colors hover:bg-action-primary-hover"
        >
          Открыть карточку объекта
        </button>
      ) : (
        <button
          type="button"
          disabled
          title="Доступно только для объектов слоёв — не для результатов поиска или введённых координат"
          className="mt-4 w-full rounded-lg bg-action-primary py-2.5 text-sm font-semibold text-text-inverse opacity-50 disabled:cursor-not-allowed"
        >
          Открыть карточку объекта
        </button>
      )}
    </div>

    {cardModalOpen && selectedFeatureLayerId && selectedFeatureId && (
      <ObjectCardModal
        layerId={selectedFeatureLayerId}
        featureId={selectedFeatureId}
        onClose={() => setCardModalOpen(false)}
      />
    )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="shrink-0 text-xs text-text-secondary">{label}</span>
      <span className="truncate text-xs font-medium text-text-primary">{value}</span>
    </div>
  );
}
